import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import {
  faArrowRight,
  faCalendarPlus,
  faList,
} from "@fortawesome/free-solid-svg-icons";
import { getDashboardToday } from "../APIs/api-routes.util";
import { DashboardTodayResponse } from "../APIs/api.types";
import { QuickDisableDayModal } from "../components/QuickDisableDayModal";
import { Alert } from "../components/ui/Alert";
import { Button } from "../components/ui/Button";
import { LoadingOverlay } from "../components/ui/PagePrimitives";
import { useAppSettings } from "../context/AppSettingsContext";
import { apiSubscribe } from "../lib/api-subscribe";

const actions = [
  {
    to: "/admin-booking",
    title: "Book appointment",
    description: "Normal or urgent booking",
    icon: faCalendarPlus,
    border: "border-l-accent-blue",
    iconClass: "bg-accent-blue-light text-accent-blue-dark",
  },
  {
    to: "/appointments",
    title: "Today's list",
    description: "View and manage appointments",
    icon: faList,
    border: "border-l-accent-emerald",
    iconClass: "bg-accent-emerald-light text-accent-emerald-dark",
  },
];

function Dashboard() {
  const { urgentEnabled } = useAppSettings();
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState<DashboardTodayResponse["data"] | null>(null);
  const [alert, setAlert] = useState<{ variant: "success" | "error"; message: string } | null>(null);
  const [disableOpen, setDisableOpen] = useState(false);

  useEffect(() => {
    return apiSubscribe<DashboardTodayResponse>(
      getDashboardToday(),
      {
        next: (body) => {
          setToday(body.data ?? null);
          setLoading(false);
        },
        error: () => {
          setToday(null);
          setLoading(false);
        },
      }
    );
  }, []);

  return (
    <>
      <LoadingOverlay show={loading} />

      {alert && (
        <Alert variant={alert.variant} onDismiss={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <div className="rounded-xl bg-header-bar p-4 text-white shadow-soft">
        <h2 className="text-sm font-semibold">Welcome back</h2>
        <p className="mt-1 text-sm text-white/85">Manage clinic appointments from your phone.</p>
      </div>

      {today && (
        <div className={`grid gap-2 ${urgentEnabled ? "grid-cols-2" : "grid-cols-2"}`}>
          <div className="rounded-xl border border-brand-100 bg-surface p-3 shadow-soft">
            <p className="text-2xs font-semibold uppercase text-content-muted">Today total</p>
            <p className="text-xl font-bold tabular-nums text-brand-800">{today.total}</p>
          </div>
          {urgentEnabled && (
            <div className="rounded-xl border border-accent-rose/30 bg-accent-rose-light p-3">
              <p className="text-2xs font-semibold uppercase text-accent-rose-dark">Urgent</p>
              <p className="text-xl font-bold tabular-nums text-accent-rose-dark">
                {today.urgent_slots.used}/{today.urgent_slots.max}
              </p>
            </div>
          )}
          <div className="rounded-xl border border-accent-emerald/30 bg-accent-emerald-light p-3">
            <p className="text-2xs font-semibold uppercase text-accent-emerald-dark">Normal</p>
            <p className="text-xl font-bold tabular-nums text-accent-emerald-dark">
              {today.normal_slots.used}/{today.normal_slots.max}
            </p>
          </div>
          <div className="rounded-xl border border-brand-100 bg-brand-50 p-3">
            <p className="text-2xs font-semibold uppercase text-brand-700">Slots left</p>
            <p className="text-sm font-bold tabular-nums text-brand-800">
              {urgentEnabled
                ? `U ${today.urgent_slots.remaining} · N ${today.normal_slots.remaining}`
                : `N ${today.normal_slots.remaining}`}
            </p>
          </div>
        </div>
      )}

      <Button
        variant="secondary"
        fullWidth
        onClick={() => setDisableOpen(true)}
        className="border-accent-amber/40 bg-accent-amber-light/40 text-accent-amber-dark hover:bg-accent-amber-light"
      >
        <FontAwesomeIcon icon={faBan} className="mr-2" />
        Quick disable a day
      </Button>

      <div className="space-y-4">
        {actions.map((action) => (
          <Link
            key={action.to}
            to={action.to}
            className={`group flex min-h-[152px] flex-col justify-between gap-4 rounded-2xl border border-border border-l-[6px] ${action.border} bg-surface p-6 shadow-soft transition-all duration-150 hover:shadow-card active:scale-[0.99]`}
          >
            <div className="flex items-start justify-between">
              <div className={`flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl text-3xl ${action.iconClass}`}>
                <FontAwesomeIcon icon={action.icon} />
              </div>
              <FontAwesomeIcon
                icon={faArrowRight}
                className="mt-2 shrink-0 text-lg text-brand-500 transition-transform group-hover:translate-x-1"
              />
            </div>
            <div className="min-w-0">
              <h3 className="text-xl font-bold leading-tight text-content-primary">{action.title}</h3>
              <p className="mt-1.5 text-base text-content-muted">{action.description}</p>
            </div>
          </Link>
        ))}
      </div>

      <QuickDisableDayModal
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        onLoading={setLoading}
        onSuccess={(msg) => setAlert({ variant: "success", message: msg })}
        onError={(msg) => setAlert({ variant: "error", message: msg })}
      />
    </>
  );
}

export default Dashboard;
